"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { useActiveStore } from "@/store/activeStore";
import { Skeleton } from "@/components/ui/Skeleton";
import { FadeIn } from "@/components/ui/FadeIn";

/**
 * 동네 레이더 미니 카드 (사용자 결정 2026-05-09, 4-3').
 *
 * 본인 매장 좌표 기준 반경 1km 카페 시장 활성도 미니뷰.
 * 클릭 시 /map 으로 이동해 풀 화면 (지도 + 슬라이더 + 핀 + 리스트).
 *
 * 4 분기 UX:
 *   비로그인       → "내 동네 카페 시장 활성도 한눈에" + 로그인 CTA
 *   매장 미등록    → 매장 등록 CTA
 *   좌표 미등록    → 매장 주소 입력 CTA
 *   정상           → 영업 중 N개 / 신규 +N / 폐업 -N + "지도 →"
 */

// 5/11 #5 — 미리보기 정보용 부분 응답 (상세는 /map 에서)
interface NeighborhoodDemoMini {
  age_20s_pct: number | null;
  age_30s_pct: number | null;
  age_40s_pct: number | null;
}
interface NeighborhoodRentMini {
  available: boolean;
  rent_krw_per_m2: number | null;
}
interface NeighborhoodLivingMini {
  available: boolean;
  hourly: { hour: number; population: number | null }[];
}
interface NeighborhoodBusinessMini {
  cafe_count: number;
  bakery_count: number;
}

interface NeighborhoodOut {
  store_id: number;
  store_name: string;
  has_coord: boolean;
  radius_km: number;
  stats: { total_active: number; new_3m: number; closed_3m: number };
  // 5/11 #5 추가 — 홈 미리보기 정보
  demographics?: NeighborhoodDemoMini | null;
  rent?: NeighborhoodRentMini | null;
  living_population?: NeighborhoodLivingMini | null;
  business?: NeighborhoodBusinessMini | null;
}

interface StoreListOut {
  items: { id: number; name: string }[];
}

const PREVIEW_RADIUS = 1.0;   // 미니 카드는 1km 고정 (풀 화면에서 슬라이더)

interface Props {
  /** true 시 외곽 section 래퍼 제거 — 부모 (MyStoreGroupSection 등) 가 padding/spacing 제어 */
  compact?: boolean;
}

export function NeighborhoodCard({ compact = false }: Props = {}) {
  const { token, isLoggedIn } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const { activeStoreId, setActiveStoreId } = useActiveStore();

  const Wrap = ({ children }: { children: React.ReactNode }) =>
    compact ? <>{children}</> : <section className="px-4 mt-6">{children}</section>;

  const [data, setData] = useState<NeighborhoodOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<"no_store" | "no_coord" | "fetch_error" | null>(null);

  useEffect(() => {
    if (!hasHydrated) return;
    if (!isLoggedIn || !token) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };

    const fetchNeighborhood = (storeId: number) => {
      setLoading(true);
      setError(null);
      axios
        .get<NeighborhoodOut>(`/api/stores/${storeId}/neighborhood?radius_km=${PREVIEW_RADIUS}`, { headers })
        .then((r) => {
          if (cancelled) return;
          setData(r.data);
          if (!r.data.has_coord) setError("no_coord");
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          const status = err?.response?.status;
          if (status === 404 || status === 403) {
            resolveAndFetch();
          } else {
            setError("fetch_error");
            setLoading(false);
          }
        });
    };

    const resolveAndFetch = () => {
      setLoading(true);
      axios
        .get<StoreListOut>("/api/stores", { headers })
        .then((r) => {
          if (cancelled) return;
          if (!r.data.items || r.data.items.length === 0) {
            setActiveStoreId(null);
            setError("no_store");
            setLoading(false);
            return;
          }
          const firstId = r.data.items[0].id;
          setActiveStoreId(firstId);
          fetchNeighborhood(firstId);
        })
        .catch(() => {
          if (!cancelled) {
            setError("fetch_error");
            setLoading(false);
          }
        });
    };

    if (activeStoreId) {
      fetchNeighborhood(activeStoreId);
    } else {
      resolveAndFetch();
    }

    return () => { cancelled = true; };
  }, [hasHydrated, isLoggedIn, token, activeStoreId, setActiveStoreId]);

  if (!hasHydrated) return null;

  // 비로그인 / 매장 미등록 / 좌표 미등록 — 숫자만 blur (사용자 결정 2026-05-10 재디자인)
  if (!isLoggedIn || error === "no_store" || error === "no_coord") {
    let ctaHref = "/login";
    let ctaLabel = "로그인 →";
    let lockMessage = "로그인하면 내 동네 카페 시장 활성도";
    if (isLoggedIn && error === "no_store") {
      ctaHref = "/store";
      ctaLabel = "매장 등록 →";
      lockMessage = "매장 등록하면 내 동네 카페 분포·신규·폐업";
    } else if (isLoggedIn && error === "no_coord") {
      ctaHref = "/store/manage";
      ctaLabel = "주소 입력 →";
      lockMessage = "매장 주소 입력하면 좌표 기준 동네 레이더";
    }
    return (
      <Wrap>
        <div className="flex items-center justify-between mb-1">
          <h2 className="section-title mb-0">내 동네 카페 레이더</h2>
          <Link href={ctaHref} className="text-xs text-primary font-semibold">{ctaLabel}</Link>
        </div>
        <FadeIn className="card">
          <p className="text-2xs text-text-sub mb-2">🔒 {lockMessage}</p>
          {/* 5/11 — 3x2 통합 grid (lock 화면도 동일 구조) */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-2xs text-text-sub">영업 중</p>
              <p className="text-lg font-bold text-text mt-0.5 blur-sm select-none">47</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">신규 (3개월)</p>
              <p className="text-lg font-bold text-emerald-500 mt-0.5 blur-sm select-none">+5</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">폐업 (3개월)</p>
              <p className="text-lg font-bold text-red-500 mt-0.5 blur-sm select-none">-3</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">20·30대</p>
              <p className="text-lg font-bold text-text mt-0.5 blur-sm select-none">44%</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">카페+베이커리</p>
              <p className="text-lg font-bold text-text mt-0.5 blur-sm select-none">128</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">평균 임대료</p>
              <p className="text-lg font-bold text-text mt-0.5 blur-sm select-none">
                25<span className="text-2xs font-normal text-text-sub ml-0.5">천원/㎡</span>
              </p>
            </div>
          </div>
        </FadeIn>
      </Wrap>
    );
  }

  if (loading || (!data && !error)) {
    return (
      <Wrap>
        <h2 className="section-title mb-3">내 동네 카페 레이더</h2>
        <FadeIn className="card">
          <div className="text-xs text-text-sub text-center py-1 mb-2">
            🗺️ 내 매장 반경 1km 카페 분석 중... 5~10초 정도 소요됩니다
          </div>
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-12 w-full" />
        </FadeIn>
      </Wrap>
    );
  }

  if (error === "fetch_error" || !data) return null;

  // 정상 — 미니뷰 (5/11 후속 재설계 — mockup 2 grid 형식)
  const { stats } = data;
  // 동네 한눈에 — 20·30대 합계 / 카페+베이커리 / 평균 임대료
  const age2030 = (() => {
    const a = data.demographics?.age_20s_pct ?? null;
    const b = data.demographics?.age_30s_pct ?? null;
    if (a == null && b == null) return null;
    return Math.round((a ?? 0) + (b ?? 0));
  })();
  const cafeBakery = (() => {
    const c = data.business?.cafe_count ?? 0;
    const b = data.business?.bakery_count ?? 0;
    return c + b > 0 ? c + b : null;
  })();
  const avgRent = data.rent?.available ? data.rent.rent_krw_per_m2 : null;

  return (
    <Wrap>
      <div className="flex items-center justify-between mb-1">
        <h2 className="section-title mb-0">내 동네 카페 레이더</h2>
        <Link href="/map" className="text-xs text-text-sub hover:text-text transition-colors">
          지도에서 자세히 →
        </Link>
      </div>

      <Link href="/map">
        <FadeIn className="card hover:bg-bg-elevated transition-colors cursor-pointer">
          <p className="text-2xs text-text-sub mb-2">반경 1km 이내 (행정안전부 + 소상공인 + KOSIS)</p>
          {/* 5/11 — 3x2 통합 grid (시장 동향 / 동네 한눈에 구분 제거) */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-2xs text-text-sub">영업 중</p>
              <p className="text-lg font-bold text-text mt-0.5">{stats.total_active.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">신규 (3개월)</p>
              <p className="text-lg font-bold text-emerald-500 mt-0.5">+{stats.new_3m}</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">폐업 (3개월)</p>
              <p className="text-lg font-bold text-red-500 mt-0.5">-{stats.closed_3m}</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">20·30대</p>
              <p className="text-lg font-bold text-text mt-0.5">{age2030 != null ? `${age2030}%` : "—"}</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">카페+베이커리</p>
              <p className="text-lg font-bold text-text mt-0.5">{cafeBakery != null ? cafeBakery.toLocaleString() : "—"}</p>
            </div>
            <div>
              <p className="text-2xs text-text-sub">평균 임대료</p>
              <p className="text-lg font-bold text-text mt-0.5">
                {avgRent != null ? (
                  <>
                    {avgRent.toFixed(0)}
                    <span className="text-2xs font-normal text-text-sub ml-0.5">천원/㎡</span>
                  </>
                ) : "—"}
              </p>
            </div>
          </div>
        </FadeIn>
      </Link>
    </Wrap>
  );
}
